import { useCallback, useEffect, useMemo, useState } from 'react'
import { Boxes, Loader2, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createGoodsReceipt,
  createStockTake,
  fetchAdjustmentReasons,
  fetchGoodsReceipts,
  fetchInventoryProducts,
  fetchLowStockProducts,
  fetchStockBalances,
  fetchStockMovements,
  fetchStockTakes,
  postGoodsReceipt,
  postStockAdjustment,
  postStockTake,
} from '@/lib/api/inventory'
import { fetchSuppliers } from '@/lib/api/suppliers'
import { cn } from '@/lib/utils'

const TABS = [
  { id: 'on_hand', label: 'On hand' },
  { id: 'movements', label: 'Movements' },
  { id: 'grn', label: 'GRN' },
  { id: 'adjust', label: 'Adjust' },
  { id: 'stock_take', label: 'Stock take' },
]

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

function formatQty(value) {
  const num = Number(value ?? 0)
  return Number.isFinite(num) ? num.toFixed(3) : '0.000'
}

export function InventoryPage() {
  const { activeBranchId } = useBranch()
  const [tab, setTab] = useState('on_hand')
  const [products, setProducts] = useState([])
  const [balances, setBalances] = useState([])
  const [lowStock, setLowStock] = useState([])
  const [movements, setMovements] = useState([])
  const [reasons, setReasons] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [grns, setGrns] = useState([])
  const [stockTakes, setStockTakes] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [adjustForm, setAdjustForm] = useState({ product_id: '', quantity_delta: '', reason_id: '', notes: '' })
  const [grnForm, setGrnForm] = useState({
    supplier_id: '',
    notes: '',
    lines: [{ product_id: '', quantity: 1, unit_cost: 0 }],
  })
  const [stockTakeForm, setStockTakeForm] = useState({
    notes: '',
    lines: [{ product_id: '', counted_quantity: 0 }],
  })

  const reload = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [
        productItems,
        balanceItems,
        lowStockItems,
        movementItems,
        reasonItems,
        supplierItems,
        grnItems,
        stockTakeItems,
      ] = await Promise.all([
        fetchInventoryProducts(),
        fetchStockBalances(),
        fetchLowStockProducts(),
        fetchStockMovements(),
        fetchAdjustmentReasons(),
        fetchSuppliers(),
        fetchGoodsReceipts(),
        fetchStockTakes(),
      ])
      setProducts(productItems)
      setBalances(balanceItems)
      setLowStock(lowStockItems)
      setMovements(movementItems)
      setReasons(reasonItems)
      setSuppliers(supplierItems.filter((item) => item.is_active))
      setGrns(grnItems)
      setStockTakes(stockTakeItems)
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to load inventory.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload, activeBranchId])

  const onHandRows = useMemo(() => {
    if (balances.length > 0) {
      return balances.map((row) => ({
        id: row.product_id,
        name: row.product?.name ?? 'Product',
        sku: row.product?.sku ?? '',
        onHand: row.quantity_on_hand,
        minStock: row.product?.min_stock ?? 0,
      }))
    }

    return products.map((product) => ({
      id: product.id,
      name: product.name,
      sku: product.sku,
      onHand: product.quantity_on_hand ?? 0,
      minStock: product.min_stock ?? 0,
    }))
  }, [balances, products])

  async function handleAdjustSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')
    try {
      await postStockAdjustment({
        product_id: Number(adjustForm.product_id),
        quantity_delta: Number(adjustForm.quantity_delta),
        reason_id: adjustForm.reason_id ? Number(adjustForm.reason_id) : null,
        notes: adjustForm.notes || undefined,
      })
      setSuccess('Stock adjusted.')
      setAdjustForm({ product_id: '', quantity_delta: '', reason_id: '', notes: '' })
      await reload()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Adjustment failed.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGrnSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')
    try {
      const grn = await createGoodsReceipt({
        supplier_id: grnForm.supplier_id ? Number(grnForm.supplier_id) : null,
        notes: grnForm.notes || undefined,
        lines: grnForm.lines
          .filter((line) => line.product_id)
          .map((line) => ({
            product_id: Number(line.product_id),
            quantity: Number(line.quantity),
            unit_cost: Number(line.unit_cost),
          })),
      })
      await postGoodsReceipt(grn.id)
      setSuccess(`GRN ${grn.grn_number} posted.`)
      setGrnForm({ supplier_id: '', notes: '', lines: [{ product_id: '', quantity: 1, unit_cost: 0 }] })
      await reload()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'GRN failed.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleStockTakeSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')
    try {
      const stockTake = await createStockTake({
        notes: stockTakeForm.notes || undefined,
        lines: stockTakeForm.lines
          .filter((line) => line.product_id)
          .map((line) => ({
            product_id: Number(line.product_id),
            counted_quantity: Number(line.counted_quantity),
          })),
      })
      await postStockTake(stockTake.id)
      setSuccess(`Stock take ${stockTake.stock_take_number} posted.`)
      setStockTakeForm({ notes: '', lines: [{ product_id: '', counted_quantity: 0 }] })
      await reload()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Stock take failed.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 lg:px-8">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Boxes className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Inventory</h1>
          <p className="text-sm text-muted-foreground">
            Stock on hand, movements, goods receipts, adjustments, and stock takes.
          </p>
        </div>
      </div>

      {lowStock.length > 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>{lowStock.length}</strong> product(s) at or below minimum stock.
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
              tab === item.id ? 'bg-primary text-primary-foreground' : 'bg-muted/70 hover:bg-muted',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {success ? <p className="text-sm text-emerald-600">{success}</p> : null}

      {loading ? (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading inventory…
        </div>
      ) : null}

      {!loading && tab === 'on_hand' ? (
        <section className="card-surface overflow-x-auto p-4">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-2 py-2">Product</th>
                <th className="px-2 py-2">SKU</th>
                <th className="px-2 py-2">On hand</th>
                <th className="px-2 py-2">Min</th>
              </tr>
            </thead>
            <tbody>
              {onHandRows.map((row) => (
                <tr key={row.id} className="border-b border-border/40">
                  <td className="px-2 py-2 font-medium">{row.name}</td>
                  <td className="px-2 py-2 text-muted-foreground">{row.sku}</td>
                  <td className={cn('px-2 py-2', Number(row.onHand) <= Number(row.minStock) && 'text-amber-600 font-semibold')}>
                    {formatQty(row.onHand)}
                  </td>
                  <td className="px-2 py-2">{row.minStock}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {!loading && tab === 'movements' ? (
        <section className="card-surface overflow-x-auto p-4">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-2 py-2">When</th>
                <th className="px-2 py-2">Product</th>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">Delta</th>
                <th className="px-2 py-2">After</th>
              </tr>
            </thead>
            <tbody>
              {movements.map((row) => (
                <tr key={row.id} className="border-b border-border/40">
                  <td className="px-2 py-2 text-muted-foreground">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-2 py-2">{row.product?.name ?? row.product_id}</td>
                  <td className="px-2 py-2 capitalize">{row.movement_type?.replace('_', ' ')}</td>
                  <td className="px-2 py-2">{formatQty(row.quantity_delta)}</td>
                  <td className="px-2 py-2">{formatQty(row.quantity_after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {!loading && tab === 'grn' ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={handleGrnSubmit} className="card-surface space-y-4 p-6">
            <h2 className="font-semibold">New goods receipt</h2>
            <select
              value={grnForm.supplier_id}
              onChange={(e) => setGrnForm((f) => ({ ...f, supplier_id: e.target.value }))}
              className={inputClass}
            >
              <option value="">Supplier (optional)</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </select>
            {grnForm.lines.map((line, index) => (
              <div key={index} className="grid gap-2 md:grid-cols-3">
                <select
                  value={line.product_id}
                  onChange={(e) => setGrnForm((f) => ({
                    ...f,
                    lines: f.lines.map((row, i) => (i === index ? { ...row, product_id: e.target.value } : row)),
                  }))}
                  className={inputClass}
                  required
                >
                  <option value="">Product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
                <input type="number" min="0.001" step="0.001" value={line.quantity} onChange={(e) => setGrnForm((f) => ({
                  ...f,
                  lines: f.lines.map((row, i) => (i === index ? { ...row, quantity: e.target.value } : row)),
                }))} className={inputClass} placeholder="Qty" required />
                <input type="number" min="0" step="0.01" value={line.unit_cost} onChange={(e) => setGrnForm((f) => ({
                  ...f,
                  lines: f.lines.map((row, i) => (i === index ? { ...row, unit_cost: e.target.value } : row)),
                }))} className={inputClass} placeholder="Unit cost" required />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setGrnForm((f) => ({
              ...f,
              lines: [...f.lines, { product_id: '', quantity: 1, unit_cost: 0 }],
            }))}>
              <Plus className="mr-1 h-4 w-4" />
              Add line
            </Button>
            <Button type="submit" disabled={submitting}>{submitting ? 'Posting…' : 'Create & post GRN'}</Button>
          </form>
          <section className="card-surface p-6">
            <h2 className="mb-3 font-semibold">Recent GRNs</h2>
            <ul className="space-y-2 text-sm">
              {grns.slice(0, 10).map((grn) => (
                <li key={grn.id} className="rounded-xl border border-border/50 px-3 py-2">
                  <div className="font-medium">{grn.grn_number}</div>
                  <div className="text-muted-foreground">{grn.status} · {grn.supplier?.name ?? 'No supplier'}</div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      ) : null}

      {!loading && tab === 'adjust' ? (
        <form onSubmit={handleAdjustSubmit} className="card-surface max-w-xl space-y-4 p-6">
          <h2 className="font-semibold">Stock adjustment</h2>
          <select value={adjustForm.product_id} onChange={(e) => setAdjustForm((f) => ({ ...f, product_id: e.target.value }))} className={inputClass} required>
            <option value="">Product</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>{product.name}</option>
            ))}
          </select>
          <input type="number" step="0.001" value={adjustForm.quantity_delta} onChange={(e) => setAdjustForm((f) => ({ ...f, quantity_delta: e.target.value }))} className={inputClass} placeholder="Quantity delta (+/-)" required />
          <select value={adjustForm.reason_id} onChange={(e) => setAdjustForm((f) => ({ ...f, reason_id: e.target.value }))} className={inputClass}>
            <option value="">Reason (optional)</option>
            {reasons.map((reason) => (
              <option key={reason.id} value={reason.id}>{reason.name}</option>
            ))}
          </select>
          <input type="text" value={adjustForm.notes} onChange={(e) => setAdjustForm((f) => ({ ...f, notes: e.target.value }))} className={inputClass} placeholder="Notes" />
          <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Post adjustment'}</Button>
        </form>
      ) : null}

      {!loading && tab === 'stock_take' ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={handleStockTakeSubmit} className="card-surface space-y-4 p-6">
            <h2 className="font-semibold">Stock take</h2>
            {stockTakeForm.lines.map((line, index) => (
              <div key={index} className="grid gap-2 md:grid-cols-2">
                <select
                  value={line.product_id}
                  onChange={(e) => setStockTakeForm((f) => ({
                    ...f,
                    lines: f.lines.map((row, i) => (i === index ? { ...row, product_id: e.target.value } : row)),
                  }))}
                  className={inputClass}
                  required
                >
                  <option value="">Product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
                <input type="number" min="0" step="0.001" value={line.counted_quantity} onChange={(e) => setStockTakeForm((f) => ({
                  ...f,
                  lines: f.lines.map((row, i) => (i === index ? { ...row, counted_quantity: e.target.value } : row)),
                }))} className={inputClass} placeholder="Counted qty" required />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setStockTakeForm((f) => ({
              ...f,
              lines: [...f.lines, { product_id: '', counted_quantity: 0 }],
            }))}>
              <Plus className="mr-1 h-4 w-4" />
              Add line
            </Button>
            <Button type="submit" disabled={submitting}>{submitting ? 'Posting…' : 'Create & post stock take'}</Button>
          </form>
          <section className="card-surface p-6">
            <h2 className="mb-3 font-semibold">Recent stock takes</h2>
            <ul className="space-y-2 text-sm">
              {stockTakes.slice(0, 10).map((take) => (
                <li key={take.id} className="rounded-xl border border-border/50 px-3 py-2">
                  <div className="font-medium">{take.stock_take_number}</div>
                  <div className="text-muted-foreground">{take.status}</div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      ) : null}
    </div>
  )
}
